/* FILE: ./frontend/src/features/canada/pages/SupplierOnboarding.jsx */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

import { supabase } from '../../../lib/supabase';
import { useCompliance } from '../../../hooks/useCompliance';
import { useAuth } from '../../../contexts/AuthContext';
import { validateBN9 } from '../../../utils/validation';

import DisputeModal from '../../common/components/DisputeModal';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

const CANADIAN_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' }
];

// ⚡ STRICT CSI MASTERFORMAT CATEGORIES
const MASTERFORMAT_CATEGORIES = [
  'Concrete & Cementitious',
  'Masonry & Stone',
  'Structural Steel & Heavy Metal',
  'Cold-Formed Metal Framing',
  'Mass Timber & Engineered Wood',
  'Rough Carpentry & Lumber',
  'Thermal & Moisture Protection',
  'Exterior Enclosure & Cladding',
  'Fenestration',
  'Earth Retention & Piling'
];

// ⚡ STANDARDIZED CAPABILITIES
const CAPABILITY_TAGS = {
  'Engineering & Pre-Construction': [
    'BIM / VDC Modeling', 'In-House Structural Engineering', 'Shop Drawing Detailing', 'Design-Assist'
  ],
  'Manufacturing & Fabrication': [
    'Custom Structural Fabrication', 'CNC Machining & Cutting', 'Roll Forming & Extrusion', 'Metal Stamping & Punching', 'Pre-Assembly / Modular Construction'
  ],
  'Welding & Treatment': [
    'CWB Certified Welding', 'Robotic Welding', 'TIG / MIG / Arc Welding', 'Hot-Dip Galvanizing', 'Powder Coating & Painting', 'Anodizing', 'Sandblasting / Surface Prep'
  ],
  'Logistics & Site': [
    'Field Erection / Site Installation', 'Just-In-Time (JIT) Delivery', 'Oversize / Heavy Haul Transport', 'Crane & Rigging Services'
  ]
};

export default function SupplierOnboarding({ userId, onProfileCreated }) {
  // --- HOOKS ---
  const { 
      checkCompliance, 
      getCategoryRule, 
      rules = { categories: [] }, 
      loading: rulesLoading 
  } = useCompliance();
  const { t } = useTranslation();

  const [showDispute, setShowDispute] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  // ⚡ EXTRACTED USER & PROFILE: Needed for the pre-fill injection
  const { user, userProfile, isSupplier, supplierId, loading: authLoading } = useAuth();

  const location = useLocation();
  const returnTo = location.state?.returnTo || sessionStorage.getItem('returnTo');

  // --- STATE ---
  const [checkingDatabase, setCheckingDatabase] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [step, setStep] = useState(1); // 1=Verify, 2=Profile
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
    
  // Verification Inputs
  const [bnInput, setBnInput] = useState('');
  const [bnError, setBnError] = useState(''); 
  const [inputType, setInputType] = useState('unknown'); // 'bn9' or 'bc_corp'
  
  // Compliance State
  const [isAutoVerified, setIsAutoVerified] = useState(false);
  const [isDateVerified, setIsDateVerified] = useState(false);
  
  // Specific Compliance States (FSD 3.2)
  const [calculatedTier, setCalculatedTier] = useState('Tier 1 (Standard)');
  const [requiredCert, setRequiredCert] = useState(null); 
  const [isForbidden, setIsForbidden] = useState(false);
  const [forbiddenReason, setForbiddenReason] = useState('');
  const [complianceStatus, setComplianceStatus] = useState({ restricted: false, reason: '' });

  // OTP Verification State
  const [originalPhone, setOriginalPhone] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contact_email: '',
    contact_person: '', 
    phone: '',
    website: '',
    province: 'ON',
    city: '',
    business_number: '',
    address: '',
    postal_code: '',
    naics_codes: [],
    incorporation_date: '', 
    categories: [], 
    capabilities: [], 
    certification_url: '' 
  });

  // --- 1. INITIALIZATION ---
  useEffect(() => {
    if (authLoading) return;

    const performCheck = async () => {
        setCheckingDatabase(true);
        const safetyTimer = setTimeout(() => {
            if (!initialCheckDone) setInitialCheckDone(true);
        }, 2000);

        try {
            if (isSupplier && supplierId) {
                await loadExistingProfile(supplierId);
            } else {
                const { data } = await supabase
                    .from('suppliers')
                    .select('id')
                    .eq('user_id', userId)
                    .maybeSingle(); 
                
                if (data) {
                    await loadExistingProfile(data.id);
                } else {
                    // ⚡ PRE-FILL FIX: Use global profile data for existing Buyers
                    setFormData(prev => ({
                        ...prev,
                        name: userProfile?.company_name || '',
                        contact_person: userProfile?.contact_person || '',
                        contact_email: user?.email || '',
                        phone: userProfile?.phone || '',
                        province: userProfile?.province || 'ON'
                    }));
                    setStep(1);
                }
            }
        } catch (err) {
            console.error("Profile check failed:", err);
            setStep(1);
        } finally {
            setInitialCheckDone(true);
            setCheckingDatabase(false);
            clearTimeout(safetyTimer);
        }
    };

    performCheck();
  }, [isSupplier, supplierId, authLoading, userId]); // eslint-disable-line

  // --- 2. LOAD PROFILE ---
  const loadExistingProfile = async (specificId) => {
    try {
      const targetId = specificId || supplierId;
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('id', targetId)
        .maybeSingle(); 

      if (data && !error) {
        setFormData({
          name: data.company_name || '',
          description: data.description || '',
          contact_email: data.contact_email || '',
          contact_person: data.contact_person || '', 
          phone: data.phone || '',
          website: data.website || '',
          province: data.province || 'ON',
          city: data.city || '',
          business_number: data.business_number || '',
          address: data.address || '',
          postal_code: data.postal_code || '',
          naics_codes: data.naics_codes || [],
          incorporation_date: data.incorporation_date || (data.year_established ? `${data.year_established}-01-01` : ''),
          capabilities: data.capabilities || [], 
          categories: data.categories || (data.category ? [data.category] : []), 
          certification_url: data.certification_url || ''
        });

        setOriginalPhone(data.phone || '');
        setBnInput(data.business_number || '');
        setIsAutoVerified(data.is_verified);
        setStep(2); 
      }
    } catch (err) {
      console.error("Error loading profile:", err);
    }
  };

  // --- 3. DYNAMIC TIER LOGIC (DATABASE DRIVEN) ---
  useEffect(() => {
    if (formData.categories.length > 0 && getCategoryRule) {
        let highestRisk = 'Tier 1 (Standard)';
        let isForbiddenLocal = false;
        let requiredCertLocal = null;
        let forbiddenReasonLocal = '';

        formData.categories.forEach(cat => {
            const rule = getCategoryRule(cat);
            if (rule) {
                if (rule.risk_level === 'prohibited') {
                    highestRisk = 'Prohibited';
                    isForbiddenLocal = true;
                    forbiddenReasonLocal = rule.reason_message;
                    requiredCertLocal = null;
                } else if (rule.risk_level === 'high_risk' && highestRisk !== 'Prohibited') {
                    highestRisk = 'Tier 2 (High Risk)';
                    isForbiddenLocal = false;
                    forbiddenReasonLocal = '';
                    requiredCertLocal = { cert: rule.reason_message, body: 'Regulatory Authority' };
                }
            }
        });

        setIsForbidden(isForbiddenLocal);
        setForbiddenReason(forbiddenReasonLocal);
        setRequiredCert(requiredCertLocal);
        setCalculatedTier(highestRisk);
    } else {
        setCalculatedTier('Tier 1 (Standard)');
        setIsForbidden(false);
        setRequiredCert(null);
    }
  }, [formData.categories, getCategoryRule]);

  // --- 4. HELPERS & HANDLERS ---
  const handleDateChange = (val) => {
    let digits = val.replace(/\D/g, '');
    if (digits.length > 8) digits = digits.slice(0, 8);
    
    if (digits.length >= 5) {
        const month = parseInt(digits.slice(4, 6), 10);
        if (month > 12) digits = digits.slice(0, 4) + '12' + digits.slice(6);
        else if (month === 0 && digits.length >= 6) digits = digits.slice(0, 4) + '01' + digits.slice(6);
    }
    
    if (digits.length >= 7) {
        const day = parseInt(digits.slice(6, 8), 10);
        if (day > 31) digits = digits.slice(0, 6) + '31';
        else if (day === 0 && digits.length === 8) digits = digits.slice(0, 6) + '01';
    }

    let formatted = digits;
    if (digits.length > 4) formatted = `${digits.slice(0, 4)}-${digits.slice(4)}`;
    if (digits.length > 6) formatted = `${formatted.slice(0, 7)}-${digits.slice(6)}`;
    setFormData(prev => ({ ...prev, incorporation_date: formatted }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'description') {
       const result = checkCompliance(formData.capabilities.join(', ') + ' ' + value, null);
       setComplianceStatus(result);
    }
  };

  const toggleCategory = (cat) => {
    if (isSupplier) return; 
    setFormData(prev => {
        const isSelected = prev.categories.includes(cat);
        if (isSelected) {
            return { ...prev, categories: prev.categories.filter(c => c !== cat) };
        } else {
            if (prev.categories.length >= 3) {
                alert(t('onboarding.max_cat_err', 'You can only select up to 3 core categories.'));
                return prev;
            }
            return { ...prev, categories: [...prev.categories, cat] };
        }
    });
  };

  const toggleCapability = (cap) => {
    setFormData(prev => {
        const nextCaps = prev.capabilities.includes(cap)
            ? prev.capabilities.filter(c => c !== cap)
            : [...prev.capabilities, cap];
        
        const result = checkCompliance(nextCaps.join(', ') + ' ' + prev.description, null);
        setComplianceStatus(result);

        return { ...prev, capabilities: nextCaps };
    });
  };

  const parseProvince = (addr) => {
    if (!addr) return 'ON';
    const match = addr.match(/,\s*([A-Z]{2})\s+/);
    return match ? match[1] : 'ON';
  };

  const parseCity = (addr) => {
    if (!addr) return '';
    const parts = addr.split(',');
    return parts.length > 1 ? parts[1].trim() : '';
  };

  const handleBnChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setBnInput(val);
    
    if (/^\d{9}$/.test(val)) {
        setInputType('bn9');
        const isValid = validateBN9(val);
        setBnError(isValid ? '' : '❌ Federal BN must pass checksum validation.');
    } else if (val.length > 3 && /^[A-Z]{1,3}\d+/.test(val)) {
        setInputType('bc_corp');
        setBnError('');
    } else {
        setInputType('unknown');
        if (val.length > 2) setBnError('');
    }
  };

  const triggerManualFallback = (message) => {
    if (window.confirm(message)) {
      setIsAutoVerified(false);
      setIsDateVerified(false);

      setFormData(prev => ({ 
          ...prev, 
          name: prev.name || userProfile?.company_name || '',
          address: '',
          city: '',
          province: prev.province || userProfile?.province || 'ON',
          incorporation_date: '',
          naics_codes: [],
          business_number: bnInput 
      }));

      setStep(2);
    } else {
      setError('❌ Verification stopped.');
    }
  };

  const handleVerifyBN = async (e) => {
    e.preventDefault();

    if (inputType === 'bn9' && !validateBN9(bnInput)) {
        setBnError('❌ Federal BN must pass checksum validation.');
        return;
    }

    setVerifying(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_BASE}/verification/business-number/${bnInput}?user_id=${userId}`);
      const govData = response.data;

      if (govData.verified) {
        setFormData(prev => ({
          ...prev,
          name: govData.legalName,
          business_number: bnInput,
          address: govData.address,
          province: govData.province || parseProvince(govData.address),
          city: govData.city || parseCity(govData.address),
          naics_codes: govData.naicsCodes || [],
          incorporation_date: govData.incorporationDate || '' 
        }));

        setIsAutoVerified(true);
        setIsDateVerified(!!govData.incorporationDate);
        setStep(2);
      } else {
        if (govData.reason === "PROHIBITED_SECTOR" || govData.reason === "ENTITY_EXPIRED") {
            setError(`⛔ ${govData.message}`);
        } else {
            triggerManualFallback("We couldn't auto-verify this entity. Enter details manually?");
        }
      }
    } catch (err) {
      if (err.response?.status === 409) {
         setError(err.response.data.error || "This Business ID is already registered.");
      } else {
         triggerManualFallback("Verification connection failed. Proceed manually?");
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleMockUpload = (e) => {
    if (e.target.files.length > 0) {
        const file = e.target.files[0];
        setTimeout(() => {
            setFormData(prev => ({ ...prev, certification_url: `uploaded://${file.name}` }));
            alert("✅ Certificate Attached Successfully");
        }, 800);
    }
  };

  // --- 6. SUBMISSION & OTP FLOW ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isForbidden) {
        alert(`⛔ REGISTRATION DENIED: ${forbiddenReason}`);
        return;
    }

    if (complianceStatus.restricted) {
      alert(`⛔ REGISTRATION BLOCKED: ${complianceStatus.reason}`);
      return;
    }

    if (requiredCert && !formData.certification_url) {
        alert(`⛔ COMPLIANCE HOLD: You MUST upload a valid ${requiredCert.cert}.`);
        return;
    }

    if (formData.categories.length === 0) {
        alert(t('onboarding.req_cat_err', 'You must select at least one MasterFormat Category.'));
        return;
    }

    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 10) {
        alert("Please enter a valid 10-digit phone number.");
        return;
    }

    if (cleanPhone && cleanPhone !== originalPhone.replace(/\D/g, '')) {
        setLoading(true);
        try {
            await axios.post(`${API_BASE}/verification/send-otp`, {
                phone: cleanPhone,
                email: formData.contact_email
            });
            setShowOtpModal(true);
        } catch (err) {
            console.error(err);
            alert("Failed to send verification SMS. Please check the number and try again.");
        } finally {
            setLoading(false);
        }
    } else {
        finalizeSubmission();
    }
  };

  const handleVerifyOtpSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);

      const cleanPhone = formData.phone.replace(/\D/g, '');

      try {
          if (otpCode.length !== 6) throw new Error("Code must be 6 digits.");
          
          await axios.post(`${API_BASE}/verification/verify-otp`, {
              user_id: userId, // ⚡ REQUIRED for Zero-Trust DB backend
              phone: cleanPhone,
              code: otpCode
          });
          
          setShowOtpModal(false);
          setOriginalPhone(cleanPhone); 
          await finalizeSubmission(); 
      } catch (err) {
          console.error(err);
          alert(err.message || "Invalid verification code.");
          setLoading(false);
      }
  };

  const finalizeSubmission = async () => {
    setLoading(true);
    const cleanPhone = formData.phone.replace(/\D/g, '');

    try {
      let establishedYear = new Date().getFullYear();
      if (formData.incorporation_date) {
         const parsedDate = new Date(formData.incorporation_date);
         if (!isNaN(parsedDate.getTime())) establishedYear = parsedDate.getFullYear();
      }

      const payload = {
            user_id: userId,
            company_name: formData.name,
            business_number: formData.business_number,
            description: formData.description,
            contact_email: formData.contact_email,
            contact_person: formData.contact_person, 
            phone: cleanPhone,
            website: formData.website,
            province: formData.province,
            city: formData.city,
            address: formData.address,
            postal_code: formData.postal_code,
            naics_codes: formData.naics_codes,
            year_established: establishedYear,
            incorporation_date: formData.incorporation_date, 
            capabilities: formData.capabilities, 
            is_verified: isAutoVerified,
            categories: formData.categories, 
            compliance_tier: calculatedTier,
            certification_url: formData.certification_url
      };

      let response;
      if (isSupplier && supplierId) {
         response = await axios.put(`${API_BASE}/suppliers/${supplierId}`, payload);
      } else {
         response = await axios.post(`${API_BASE}/suppliers`, payload);
      }

      if (response.status === 201 || response.status === 200) {
          try {
              const CURRENT_TOS_VERSION = 'v1.0-feb-2026';
              const CURRENT_PRIVACY_VERSION = 'v1.0-feb-2026';
              await axios.post(`${API_BASE}/legal/consent`, {
                  user_id: userId,
                  agreements: [
                      { type: 'terms_of_service', version: CURRENT_TOS_VERSION },
                      { type: 'privacy_policy', version: CURRENT_PRIVACY_VERSION },
                      { type: 'supplier_agreement', version: 'v1.0' }
                  ]
              });
          } catch (legalError) {
              console.warn("⚠️ Failed to log legal consent:", legalError);
          }

         setIsSuccess(true);
         setLoading(false);
         try {
             await supabase.from('profiles').update({
                 role: 'supplier',
                 province: formData.province
             }).eq('id', userId);
         } catch (err) { console.warn("Profile update warning:", err); }
         if (onProfileCreated) onProfileCreated(response.data.id);

         sessionStorage.removeItem('returnTo');

         if (returnTo) {
             window.location.href = returnTo;
         } else {
             window.location.href = '/inventory';
         }
      }
    } catch (error) {
      const backendMsg = error.response?.data?.message;
      const errorCode = error.response?.data?.code; 
      const backendError = error.response?.data?.error;

      if (error.response?.status === 403 && errorCode === 'RESTRICTED_ENTITY') {
          setBlockReason(backendMsg || "Compliance Violation Detected");
          setShowDispute(true);
      } else if (backendMsg) {
          alert(`⛔ REGISTRATION FAILED: ${backendMsg}`);
      } else {
          alert(`❌ Error: ${backendError || error.message}`);
      }
      setLoading(false);
    }
  };

  if (authLoading || checkingDatabase || !initialCheckDone) {
    return (
        <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg border border-blue-100 max-w-4xl mx-auto my-8 relative">
      
      <div className="text-center mb-8">
        <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">🏢</span>
        </div>
        <h2 className="text-2xl font-bold text-slate-800">
          {step === 2 || (isSupplier && supplierId) ? t('onboarding.title_profile') : t('onboarding.title_verify')}
        </h2>
        <p className="text-slate-600 mt-2">
          {step === 2 || (isSupplier && supplierId) ? t('onboarding.desc_profile') : t('onboarding.desc_verify')}
        </p>
      </div>

      {step === 1 && (
        <form onSubmit={handleVerifyBN} className="max-w-md mx-auto space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
                {t('onboarding.bn_label')}
            </label>
            <div className="relative">
              <input 
                type="text" 
                required 
                maxLength="15" 
                className={`w-full text-center text-2xl tracking-widest font-mono rounded border p-3 focus:ring-2 uppercase ${bnError ? 'border-red-500 ring-red-200' : 'border-slate-300 focus:ring-blue-500'}`} 
                placeholder={t('onboarding.bn_ph')} 
                value={bnInput} 
                onChange={handleBnChange} 
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                 {((inputType === 'bn9' && !bnError) || (inputType === 'bc_corp')) && <span className="text-green-500 text-xl">✓</span>}
              </div>
            </div>
            
            <div className="text-center mt-2 h-5">
                {inputType === 'bn9' && <span className="text-xs text-blue-600 font-bold">{t('onboarding.fed_bn_detected')}</span>}
                {inputType === 'bc_corp' && <span className="text-xs text-purple-600 font-bold">{t('onboarding.bc_corp_detected')}</span>}
            </div>

            {bnError && <p className="text-red-600 text-xs mt-1 font-bold text-center">{bnError}</p>}
          </div>

          {error && <div className="bg-red-50 text-red-700 p-3 rounded text-sm text-center border border-red-200">{error}</div>}
          
          <button 
            type="submit" 
            disabled={verifying || !!bnError || bnInput.length < 5} 
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50"
          >
            {verifying ? t('onboarding.verifying') : t('onboarding.verify_btn')}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit} className="space-y-8 animate-fade-in">
          
          <div className={`p-4 rounded-lg border ${isSupplier ? 'bg-gray-100 border-gray-300' : (isAutoVerified ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200')}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-bold text-slate-500 uppercase">
                  {isSupplier ? t('onboarding.reg_identity') : (isAutoVerified ? t('onboarding.verified_gov') : t('onboarding.manual_details'))}
              </h3>
              {isAutoVerified && <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-bold">{t('onboarding.verified')}</span>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{t('onboarding.legal_name')}</label>
                <input type="text" required readOnly={isSupplier || isAutoVerified} className={`w-full rounded border p-2 ${isSupplier || isAutoVerified ? 'bg-gray-100 cursor-not-allowed font-bold text-slate-600' : 'bg-white'}`} value={formData.name} onChange={e => handleInputChange('name', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{t('onboarding.business_number')}</label>
                <input type="text" required readOnly={isSupplier} className={`w-full rounded border p-2 ${isSupplier ? 'bg-gray-100 text-slate-500 cursor-not-allowed' : 'bg-white'}`} value={formData.business_number || bnInput} />
              </div>
            </div>
            
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
               <div>
                 <label className="block text-xs font-semibold text-slate-500 mb-1">{t('onboarding.city')}</label>
                 <input type="text" required readOnly={isSupplier || isAutoVerified} className={`w-full rounded border p-2 ${isSupplier || isAutoVerified ? 'bg-gray-100 text-slate-500' : 'bg-white'}`} value={formData.city} onChange={e => handleInputChange('city', e.target.value)} />
               </div>
               <div>
                 <label className="block text-xs font-semibold text-slate-500 mb-1">{t('onboarding.province', 'Province')}</label>
                 <select
                   required
                   disabled={isSupplier}
                   className={`w-full rounded border p-2 ${isSupplier ? 'bg-gray-100 text-slate-500 cursor-not-allowed' : 'bg-white'}`}
                   value={formData.province}
                   onChange={e => handleInputChange('province', e.target.value)}
                 >
                   {CANADIAN_PROVINCES.map(p => (
                     <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
                   ))}
                 </select>
               </div>
               <div>
                 <label className="block text-xs font-semibold text-slate-500 mb-1">{t('onboarding.inc_date')}</label>
                 <input type="text" readOnly={isSupplier || isDateVerified} className={`w-full rounded border p-2 ${isSupplier || isDateVerified ? 'bg-gray-100 text-slate-500' : 'bg-white'}`} value={formData.incorporation_date} onChange={e => handleDateChange(e.target.value)} placeholder="YYYY-MM-DD" />
               </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
               <label className="block text-sm font-semibold text-slate-700 mb-1">Your Full Name</label>
               <input required type="text" className="w-full rounded border-slate-300 p-2 border" value={formData.contact_person} onChange={e => handleInputChange('contact_person', e.target.value)} placeholder="John Doe" />
            </div>
            <div>
               <label className="block text-sm font-semibold text-slate-700 mb-1">{t('onboarding.biz_email')}</label>
               <input required type="email" className="w-full rounded border-slate-300 p-2 border" value={formData.contact_email} onChange={e => handleInputChange('contact_email', e.target.value)} placeholder="sales@company.com" />
            </div>
            <div>
               <label className="block text-sm font-semibold text-slate-700 mb-1">{t('onboarding.phone')}</label>
               <input required type="tel" className="w-full rounded border-slate-300 p-2 border" value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} placeholder="1234567890" />
            </div>
          </div>

          <div className="space-y-4 bg-gray-50 p-6 rounded-xl border border-gray-200">
            <h3 className="text-sm font-bold text-slate-700 uppercase flex items-center justify-between">
                <span>{t('onboarding.industry_compliance')}</span>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">{formData.categories.length}/3 Selected</span>
            </h3>
            
            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">{t('onboarding.primary_category')}</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {MASTERFORMAT_CATEGORIES.map(cat => {
                        const isSelected = formData.categories.includes(cat);
                        return (
                            <label 
                                key={cat} 
                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                    isSelected 
                                    ? 'bg-blue-50 border-blue-500' 
                                    : (formData.categories.length >= 3 && !isSelected) || isSupplier 
                                        ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed' 
                                        : 'bg-white border-gray-300 hover:border-blue-300'
                                }`}
                            >
                                <input 
                                    type="checkbox" 
                                    className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 disabled:opacity-50"
                                    checked={isSelected}
                                    disabled={(formData.categories.length >= 3 && !isSelected) || isSupplier}
                                    onChange={() => toggleCategory(cat)}
                                />
                                <span className={`text-sm ${isSelected ? 'text-blue-900 font-bold' : 'text-gray-700 font-medium'}`}>
                                    {cat}
                                </span>
                            </label>
                        );
                    })}
                </div>
                {isSupplier && <p className="text-xs text-gray-500 mt-3">{t('onboarding.locked_note')}</p>}
            </div>

            {isForbidden && (
                <div className="bg-red-600 text-white p-6 rounded-lg shadow animate-pulse mt-6">
                  <div className="flex items-center gap-4">
                        <span className="text-4xl">⛔</span>
                        <div>
                            <h3 className="text-lg font-bold uppercase tracking-wide">{t('onboarding.reg_blocked')}</h3>
                            <p className="font-medium mt-1">{forbiddenReason}</p>
                            <p className="text-sm opacity-80 mt-2">{t('onboarding.policy_violation')}</p>
                        </div>
                   </div>
                </div>
            )}

            {!isForbidden && requiredCert && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded animate-fade-in mt-6">
                    <div className="flex items-start">
                       <span className="text-2xl mr-3">🛡️</span>
                        <div className="w-full">
                            <h3 className="text-sm font-bold text-amber-900 uppercase">{t('onboarding.cert_req')}</h3>
                            <p className="text-xs text-amber-800 mt-1">
                                {requiredCert.cert}
                            </p>
                            <div className="mt-2 bg-white p-2 rounded border border-amber-200 text-xs">
                                <span className="font-bold text-amber-700 block">{t('onboarding.cert_upload_req')}</span>
                                <span className="text-gray-500">{t('onboarding.authority')}: {requiredCert.body}</span>
                            </div>
                            <div className="mt-3">
                                <label className="block text-xs font-bold text-amber-900 mb-1">{t('onboarding.upload_cert')}</label>
                                <input type="file" onChange={handleMockUpload} className="w-full text-sm text-slate-500" />
                                {formData.certification_url && <span className="text-green-700 text-xs font-bold mt-1 block">{t('onboarding.file_attached')}</span>}
                            </div>
                        </div>
                    </div>
                </div>
            )}
          </div>

          <div>
            <div className="mb-4 border-b border-gray-200 pb-2">
                <h3 className="text-sm font-bold text-slate-700">{t('onboarding.capabilities')}</h3>
                <p className="text-xs text-gray-500 mt-1">{t('onboarding.desc_ph')}</p>
            </div>
            
            <div className="space-y-6">
                {Object.entries(CAPABILITY_TAGS).map(([groupName, tags]) => (
                    <div key={groupName}>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{groupName}</h4>
                        <div className="flex flex-wrap gap-2">
                            {tags.map(tag => {
                                const isSelected = formData.capabilities.includes(tag);
                                return (
                                    <button
                                        type="button"
                                        key={tag}
                                        onClick={() => toggleCapability(tag)}
                                        className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 ${
                                            isSelected 
                                            ? 'bg-blue-100 border-blue-500 text-blue-800 font-bold shadow-sm' 
                                            : 'bg-white border-gray-300 text-gray-600 hover:border-blue-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {isSelected && <span className="mr-1">✓</span>}
                                        {tag}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {complianceStatus.restricted && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm font-bold animate-pulse">
                    {t('onboarding.restricted')} {complianceStatus.reason}
                </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">{t('onboarding.comp_desc')}</label>
            <textarea required rows={4} className={`w-full rounded border p-3 border ${complianceStatus.restricted ? 'border-red-500 bg-red-50' : 'border-slate-300 focus:ring-blue-500'}`} value={formData.description} onChange={e => handleInputChange('description', e.target.value)} placeholder="Describe your facility, history, and major projects..." />
          </div>

          <div className="flex space-x-3 pt-4 border-b border-gray-200 pb-8">
            {!isSupplier && (
                <button type="button" onClick={() => setStep(1)} className="px-6 py-3 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium">{t('onboarding.back')}</button>
            )}
         
            {isSuccess ? (
                <button 
                    type="button"
                    onClick={() => window.location.href = returnTo ? returnTo : '/inventory'}
                    className="flex-1 bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition-colors shadow-md animate-pulse"
                 >
                    {t('onboarding.saved')}
                </button>
            ) : (
                <button 
                    type="submit" 
                    disabled={loading || isForbidden || complianceStatus.restricted || (requiredCert && !formData.certification_url) || formData.categories.length === 0} 
                    className={`flex-1 text-white py-3 rounded-lg font-bold shadow-md transition-all 
                        ${isForbidden || complianceStatus.restricted ? 'bg-red-600 cursor-not-allowed' 
                        : (requiredCert && !formData.certification_url) || formData.categories.length === 0 ? 'bg-amber-500 cursor-not-allowed' 
                        : isAutoVerified ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
                    } disabled:opacity-50`}
                >
                    {isForbidden 
                        ? t('onboarding.reg_denied') 
                        : complianceStatus.restricted 
                            ? t('onboarding.blocked_prohib') 
                            : formData.categories.length === 0
                                ? '⚠️ Select at least 1 Category'
                                : (requiredCert && !formData.certification_url) 
                                    ? t('onboarding.upload_to_cont')
                                    : (loading ? (isSupplier ? t('onboarding.updating') : t('onboarding.creating')) : (isSupplier ? t('onboarding.update_profile') : t('onboarding.complete_reg')))
                    }
                </button>
            )}
          </div>
        </form>
      )} 

      {/* DISPUTE MODAL */}
      {showDispute && (
        <DisputeModal 
            reason={blockReason} 
            companyName={formData.name}
            onClose={() => setShowDispute(false)} 
        />
      )}

      {/* OTP MODAL */}
      {showOtpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl relative animate-fadeIn text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-6">
                    <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.verify_phone_title', 'Verify Phone')}</h3>
                <p className="text-gray-600 mb-6 text-sm">
                    {t('auth.verify_phone_desc', 'You entered a new number. We sent a code to')} <span className="font-semibold">{formData.phone}</span>
                </p>

                <form onSubmit={handleVerifyOtpSubmit}>
                    <input
                        type="text"
                        maxLength="6"
                        required
                        autoFocus
                        className="block w-full text-center text-3xl tracking-[0.5em] py-4 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 mb-6"
                        placeholder="------"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                        disabled={loading}
                    />
                    
                    <button
                        type="submit"
                        disabled={loading || otpCode.length !== 6}
                        className={`w-full flex justify-center py-3 px-4 rounded-lg shadow-sm font-bold text-white transition-all
                            ${loading || otpCode.length !== 6 ? 'bg-gray-400 cursor-not-allowed opacity-75' : 'bg-blue-600 hover:bg-blue-700'}
                        `}
                    >
                        {loading ? t('auth.verifying', 'Verifying...') : t('auth.verify_create_btn', 'Verify & Save Profile')}
                    </button>
                </form>
                
                <button 
                    type="button"
                    onClick={() => { setShowOtpModal(false); setLoading(false); }}
                    className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
                    disabled={loading}
                >
                    {t('auth.change_phone', 'Cancel / Edit Number')}
                </button>
            </div>
        </div>
      )}
      
    </div>
  );
}